import * as React from 'react';
import { useState, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/pro-regular-svg-icons';
import Card from '../../../sharedComponents/core/Cards/Card';
import { Theme } from 'helpers/theme';
import UserUpdate from './UserUpdate';
import CardPagination from './CardPagination';
import { ActivityFeedCard_company } from './__generated__/ActivityFeedCard_company.graphql';

type ActivityType =
  | 'all'
  | 'completedCourse'
  | 'failedCourse'
  | 'newCourse'
  | 'activated';

const FILTERS: ReadonlyArray<{ key: ActivityType; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'completedCourse', label: 'Completed' },
  { key: 'failedCourse', label: 'Failed' },
  { key: 'newCourse', label: 'Booked' },
  { key: 'activated', label: 'New' }
];

const useStyles = createUseStyles((theme: Theme) => ({
  cardRoot: {
    height: '100%'
  },
  card: {
    padding: '22px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    height: '100%',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  iconContainer: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: '#F4F5F7',
    borderRadius: 8,
    fontSize: 14
  },
  title: {
    fontWeight: 800,
    fontSize: 14,
    color: theme.colors.primaryBlack
  },
  description: {
    fontSize: 12,
    color: '#6A728F',
    marginTop: 2
  },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  pill: {
    fontSize: 12,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 999,
    cursor: 'pointer',
    background: '#F4F5F7',
    color: '#6A728F',
    transition: 'background 0.15s, color 0.15s',
    '&:hover': {
      background: '#EBEDF2'
    }
  },
  pillActive: {
    background: '#0950ee',
    color: theme.colors.primaryWhite,
    '&:hover': {
      background: '#0a4cdf'
    }
  },
  list: {
    flex: 1,
    minHeight: 0
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 16px',
    textAlign: 'center'
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#3d4566'
  },
  emptySubtitle: {
    fontSize: 12,
    color: '#9aa0b8'
  },
}));

type Props = {
  company: ActivityFeedCard_company;
  className?: string;
};

function ActivityFeedCard({ company, className }: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const pageSize = 6;
  const [filter, setFilter] = useState<ActivityType>('all');
  const [page, setPage] = useState(0);

  const edges = company?.activity?.edges ?? [];

  const filtered = useMemo(() => {
    if (filter === 'all') return edges;
    return edges.filter((e) => e?.type === filter);
  }, [edges, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const start = safePage * pageSize;
  const visible = filtered.slice(start, start + pageSize);

  return (
    <Card
      className={classNames(classes.cardRoot, className)}
      padding="none"
    >
      <div className={classes.card}>
        <div className={classes.header}>
          <div className={classes.iconContainer}>
            <FontAwesomeIcon icon={faBell} color="#6A728F" />
          </div>
          <div>
            <div className={classes.title}>Activity</div>
            <div className={classes.description}>
              Recent updates across your team
            </div>
          </div>
        </div>

        <div className={classes.filterRow}>
          {FILTERS.map((f) => (
            <div
              key={f.key}
              className={classNames(classes.pill, {
                [classes.pillActive]: filter === f.key
              })}
              onClick={() => {
                setFilter(f.key);
                setPage(0);
              }}
            >
              {f.label}
            </div>
          ))}
        </div>

        <div className={classes.list}>
          {filtered.length === 0 ? (
            <div className={classes.empty}>
              <span className={classes.emptyTitle}>Nothing to see here!</span>
              <span className={classes.emptySubtitle}>
                {filter === 'all'
                  ? 'No recent activity for your team.'
                  : 'No activity matching this filter.'}
              </span>
            </div>
          ) : (
            visible.map(
              (act, i) => act && <UserUpdate key={start + i} activity={act} />
            )
          )}
        </div>

        <CardPagination
          page={safePage}
          pageSize={pageSize}
          totalItems={filtered.length}
          onChange={setPage}
        />
      </div>
    </Card>
  );
}

export default createFragmentContainer(ActivityFeedCard, {
  company: graphql`
    fragment ActivityFeedCard_company on Company {
      activity(page: { limit: 80 }) {
        edges {
          type
          ...UserUpdate_activity
        }
      }
    }
  `
});
